import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, LinearProgress, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import styles from './progressBar.module.css';
import { getISOWeek } from 'date-fns';

const StatusProgressBar = ({ status, value, total, color, showPercentage, isRmpmAlert }) => {
    const safeTotal = Number(total);
    const safeValue = Number(value);
    const percentage = safeTotal > 0 && Number.isFinite(safeValue)
        ? Math.round((safeValue / safeTotal) * 100)
        : 0;

    const CustomLinearProgress = styled(LinearProgress)({
        height: 7,
        borderRadius: "0px 3px 3px 0",
        backgroundColor: '#e0e0e0',
        '& .MuiLinearProgress-bar': {
            backgroundColor: color,
            borderRadius: "0px 3px 3px 0",
        },
    });

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: 1, // Adds spacing between elements
        }}>
            <Typography
                variant="body2"
                sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    width: isRmpmAlert ? '110px' : '62px',
                    minWidth: isRmpmAlert ? '110px' : '62px',
                    textAlign: isRmpmAlert ? 'left' : 'right',
                    whiteSpace: isRmpmAlert ? 'nowrap' : 'normal',
                }}
            >
                {status}
            </Typography>

            <Box sx={{ flex: 1, mx: 1 }}>
                <CustomLinearProgress variant="determinate" value={percentage} />
            </Box>

            {/* Value (Fixed Width) */}
            <Typography variant="body2"
                sx={{
                    fontSize: '12px',
                    width: '30px',
                    textAlign: 'left',
                }}>
                {showPercentage ? `${percentage}%` : value}
            </Typography>
        </Box>
    );
};

const ProgressBarChart = ({ label, data, type }) => {
    const currentWeek = getISOWeek(new Date());
    const normalizedLabel = String(label || '').toLowerCase();
    const isRmpmAlert = type === "RMPM Alert";
    const isSegmentation = normalizedLabel.includes("segmentation");
    const isPredictedAccuracy = normalizedLabel.includes("predicted accuracy");
    const alertStatusKeys = [
        { label: "Overdue", key: "overdue", color: "#4caf50" },
        { label: "Closed", key: "closed", color: "#4caf50" },
        { label: "Accepted", key: "accepted", color: "#4caf50" },
        { label: "Actionable Alerts", key: "total", color: "#4caf50" }
    ];

    const segmentationStatusKeys = [
        { label: "Agile", color: "#00B190" },
        { label: "Star", color: "#00D7C4" },
        { label: "Nurture", color: "#005EEF" },
        { label: "Lean", color: "#00B2FF" },
    ];

    const PredictedaccuracyStatusKeys = [
        { label: `Week ${currentWeek - 4}`, color: "#00B190" },
        { label: `Week ${currentWeek - 3}`, color: "#00D7C4" },
        { label: `Week ${currentWeek - 2}`, color: "#005EEF" },
        { label: `Week ${currentWeek - 1}`, color: "#00B2FF" },
    ];

    let statuses = [];
    let total = 100;

    if (normalizedLabel.includes("alert status")) {
        statuses = alertStatusKeys.filter(status => data?.[status.label] !== undefined);
        if (isRmpmAlert) {
            const normalizedTotal = Number(data?.Total ?? data?.total ?? data?.["Actionable Alerts"]);
            if (normalizedTotal > 0) {
                total = normalizedTotal;
            } else {
                const statusValues = statuses.map((status) => Number(data?.[status.label]) || 0);
                total = Math.max(...statusValues, 1);
            }
        } else {
            total = Number(data?.Total) || 0;
        }
    } else if (isSegmentation) {
        statuses = segmentationStatusKeys;
        total = segmentationStatusKeys.reduce((sum, status) => sum + (data?.[status.label] || 0), 0);
    } 
    else if(isPredictedAccuracy){
        statuses = PredictedaccuracyStatusKeys;
        total = 100;
    }
    return (
        <Box sx={{ width: '100%', maxWidth: '500px' }}>
            {(isSegmentation || isPredictedAccuracy) && (
                <span className={styles.label} key={`seg-${label}`}>{label}</span>
            )}
            <Stack marginTop="1px">
                {statuses.map((status, index) => (
                    <StatusProgressBar
                        key={`key-${index * 2}`}
                        status={status.label}
                        value={status.label === 'Week 40' ? 97 : data?.[status.label] || 0}
                        total={total}
                        color={status.color}
                        showPercentage={isSegmentation || isPredictedAccuracy}
                        isRmpmAlert={isRmpmAlert}
                    />
                ))}
            </Stack>
        </Box>
    );
};

ProgressBarChart.propTypes = {
    label: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    type: PropTypes.string,
};

StatusProgressBar.propTypes = {
    status: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    color: PropTypes.string.isRequired,
    showPercentage: PropTypes.bool.isRequired,
    isRmpmAlert: PropTypes.bool.isRequired,
};

export default ProgressBarChart;